import { CallMethodEnums } from '@/types/api';
import {
  EvaluationCreateReq,
  EvaluationCreateRes,
  EvaluationGetParams,
  EvaluationGetRes,
  EvaluationRunsListParams,
  EvaluationRunsListRes,
  EvaluationsGetParams,
  EvaluationsGetRes,
  EvaluationsMigrateReq,
  EvaluationsMigrateRes,
  EvaluationUpdateReq,
  EvaluationUpdateRes,
  Example,
  MetricProviderCreateReq,
  MetricProviderCreateRes,
  MetricProviderGetRes,
  MetricProvidersDeprecateReq,
  MetricProvidersDeprecateRes,
  MetricProvidersGetParams,
  MetricProvidersGetRes,
  MetricProvidersMigrateReq,
  MetricProvidersMigrateRes,
  MetricProviderVersionCloneReq,
  MetricProviderVersionCloneRes,
  MetricProviderVersionCreateReq,
  MetricProviderVersionCreateRes,
  MetricProviderVersionGetRes,
  MetricProviderVersionUpdateReq,
  MetricProviderVersionUpdateRes,
  RunCreateReq,
  RunCreateRes,
  RunGetRes,
  RunResultsCompareReq,
  RunResultsCompareRes,
  RunResultsCountRes,
  RunResultsCountTotalParams,
  RunResultsCountTotalRes,
  RunResultsGetRes,
  RunResultsListParams,
  RunResultsListRes,
  RunsGetParams,
  RunsGetRes,
  ScheduleCreateReq,
  ScheduleGetRes,
  ScheduleResponse,
  SchedulesDeleteReq,
  SchedulesDeleteRes,
  SchedulesGetParams,
  SchedulesGetRes,
  SchedulesPauseReq,
  SchedulesPauseRes,
  SchedulesResumeReq,
  SchedulesResumeRes,
  SourceCreateReq,
  SourceCreateRes,
  SourceDiagnosticsReq,
  SourceDiagnosticsRes,
  SourceGetRes,
  SourcePreviewGetRes,
  SourcesSearchParams,
  SourcesSearchRes,
  SourceValidateRes,
  SourceVersionCreateReq,
  SourceVersionCreateRes,
  SourceVersionUpdateReq,
  SourceVersionValidateReq,
  SourceVersionValidateRes
} from '@/types/evals';
import { callJson, executeFetch } from './utils';

// ============================================================================
// EVALUATIONS
// ============================================================================

/**
 * List evaluations with filtering and pagination.
 * @param params - Optional query parameters for filtering, sorting, and pagination
 * @returns Paginated list of evaluations
 */
export const evaluationsGet = async( params?: EvaluationsGetParams ): Promise<EvaluationsGetRes> => {
  return await callJson<unknown, EvaluationsGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/v2/evals/evaluations',
    params
  } );
};

/**
 * Get a single evaluation by ID.
 * @param evaluationId - Evaluation ID
 * @param params - Optional query parameters (include_details)
 * @returns Single evaluation with optional details
 */
export const evaluationGet = async(
  evaluationId: string,
  params?: EvaluationGetParams
): Promise<EvaluationGetRes> => {
  return await callJson<unknown, EvaluationGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/evaluations/${ evaluationId }`,
    params
  } );
};

/**
 * Create a new evaluation.
 * @param body - Evaluation creation request
 * @returns Created evaluation
 */
export const evaluationCreate = async( body: EvaluationCreateReq ): Promise<EvaluationCreateRes> => {
  return await callJson<EvaluationCreateReq, EvaluationCreateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/evaluations'
  } );
};

/**
 * Update an evaluation (currently supports deprecation status).
 * @param evaluationId - Evaluation ID
 * @param body - Update request (is_deprecated)
 * @returns Updated evaluation
 */
export const evaluationUpdate = async(
  evaluationId: string,
  body: EvaluationUpdateReq
): Promise<EvaluationUpdateRes> => {
  return await callJson<EvaluationUpdateReq, EvaluationUpdateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.PATCH,
    route: `/v2/evals/evaluations/${ evaluationId }`
  } );
};

/**
 * Migrate evaluations to new metric provider versions.
 * @param body - Migration request with metric version replacements
 * @returns Migration results (succeeded and failed evaluations)
 */
export const evaluationsMigrate = async(
  body: EvaluationsMigrateReq
): Promise<EvaluationsMigrateRes> => {
  return await callJson<EvaluationsMigrateReq, EvaluationsMigrateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/evaluations/migrate'
  } );
};

/**
 * List runs for a specific evaluation.
 * @param evaluationId - Evaluation ID
 * @param params - Optional query parameters for filtering, sorting, and pagination
 * @returns Paginated list of runs
 */
export const evaluationRunsList = async(
  evaluationId: string,
  params?: EvaluationRunsListParams
): Promise<EvaluationRunsListRes> => {
  return await callJson<unknown, EvaluationRunsListRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/evaluations/${ evaluationId }/runs`,
    params
  } );
};

/**
 * Create/start a new run for an evaluation.
 * @param evaluationId - Evaluation ID
 * @param body - Run creation request
 * @returns Created run
 */
export const evaluationRunCreate = async(
  evaluationId: string,
  body: RunCreateReq
): Promise<RunCreateRes> => {
  return await callJson<RunCreateReq, RunCreateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/evaluations/${ evaluationId }/runs`
  } );
};

// ============================================================================
// METRIC PROVIDERS
// ============================================================================

/**
 * List/search metric providers with filtering and pagination.
 * @param params - Optional query parameters for filtering, sorting, and pagination
 * @returns Paginated list of metric providers
 */
export const metricProvidersGet = async(
  params?: MetricProvidersGetParams
): Promise<MetricProvidersGetRes> => {
  return await callJson<unknown, MetricProvidersGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/v2/evals/metric-providers',
    params
  } );
};

/**
 * Get a single metric provider by ID.
 * @param metricProviderId - Metric provider ID
 * @returns Single metric provider with all versions
 */
export const metricProviderGet = async(
  metricProviderId: string
): Promise<MetricProviderGetRes> => {
  return await callJson<unknown, MetricProviderGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/metric-providers/${ metricProviderId }`
  } );
};

/**
 * Create a new metric provider.
 * @param body - Metric provider creation request
 * @returns Created metric provider
 */
export const metricProviderCreate = async(
  body: MetricProviderCreateReq
): Promise<MetricProviderCreateRes> => {
  return await callJson<MetricProviderCreateReq, MetricProviderCreateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/metric-providers'
  } );
};

/**
 * Get a specific version of a metric provider.
 * @param metricProviderId - Metric provider ID
 * @param version - Version number
 * @returns Metric provider version configuration
 */
export const metricProviderVersionGet = async(
  metricProviderId: string,
  version: number
): Promise<MetricProviderVersionGetRes> => {
  return await callJson<unknown, MetricProviderVersionGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/metric-providers/${ metricProviderId }/versions/${ version }`
  } );
};

/**
 * Create a new version of a metric provider.
 * @param metricProviderId - Metric provider ID
 * @param body - Version creation request
 * @returns Created version configuration
 */
export const metricProviderVersionCreate = async(
  metricProviderId: string,
  body: MetricProviderVersionCreateReq
): Promise<MetricProviderVersionCreateRes> => {
  return await callJson<MetricProviderVersionCreateReq, MetricProviderVersionCreateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/metric-providers/${ metricProviderId }/versions`
  } );
};

/**
 * Update a metric provider version (currently supports deprecation status).
 * @param metricProviderId - Metric provider ID
 * @param version - Version number
 * @param body - Update request (is_deprecated)
 * @returns Updated version configuration
 */
export const metricProviderVersionUpdate = async(
  metricProviderId: string,
  version: number,
  body: MetricProviderVersionUpdateReq
): Promise<MetricProviderVersionUpdateRes> => {
  return await callJson<MetricProviderVersionUpdateReq, MetricProviderVersionUpdateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.PATCH,
    route: `/v2/evals/metric-providers/${ metricProviderId }/versions/${ version }`
  } );
};

/**
 * Clone a metric provider version.
 * @param metricProviderId - Metric provider ID
 * @param version - Version number to clone
 * @param body - Clone configuration
 * @returns Cloned metric provider
 */
export const metricProviderVersionClone = async(
  metricProviderId: string,
  version: string,
  body: MetricProviderVersionCloneReq
): Promise<MetricProviderVersionCloneRes> => {
  return await callJson<MetricProviderVersionCloneReq, MetricProviderVersionCloneRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/metric-providers/${ metricProviderId }/versions/${ version }/clone`
  } );
};

/**
 * Bulk deprecate/undeprecate metric provider versions by model.
 * @param body - Bulk deprecation request
 * @returns Affected versions and evaluations
 */
export const metricProvidersDeprecate = async(
  body: MetricProvidersDeprecateReq
): Promise<MetricProvidersDeprecateRes> => {
  return await callJson<MetricProvidersDeprecateReq, MetricProvidersDeprecateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/metric-providers/deprecate'
  } );
};

/**
 * Migrate metric providers to a new LLM model.
 * @param body - Migration request
 * @returns Migration results (succeeded, failed, skipped)
 */
export const metricProvidersMigrate = async(
  body: MetricProvidersMigrateReq
): Promise<MetricProvidersMigrateRes> => {
  return await callJson<MetricProvidersMigrateReq, MetricProvidersMigrateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/metric-providers/migrate'
  } );
};

// ============================================================================
// RUNS
// ============================================================================

/**
 * List runs with optional filtering.
 * @param params - Query parameters for filtering
 * @returns List of runs
 */
export const runsGet = async( params?: RunsGetParams ): Promise<RunsGetRes> => {
  return await callJson<unknown, RunsGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/v2/evals/runs',
    params
  } );
};

/**
 * Get a single run by ID with optional metric details.
 * @param runId - Run ID
 * @returns Run with optional details
 */
export const runGet = async( runId: string ): Promise<RunGetRes> => {
  return await callJson<unknown, RunGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/runs/${ runId }`
  } );
};

/**
 * Get results for a specific run.
 * @param runId - Run ID
 * @returns Run results
 */
export const runResultsGet = async( runId: string ): Promise<RunResultsGetRes> => {
  return await callJson<unknown, RunResultsGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/runs/${ runId }/results`
  } );
};

/**
 * Get result count for a specific run.
 * @param runId - Run ID
 * @returns Result count
 */
export const runResultsCount = async( runId: string ): Promise<RunResultsCountRes> => {
  return await callJson<unknown, RunResultsCountRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/runs/${ runId }/results/count`
  } );
};

/**
 * Cancel a running evaluation.
 * @param runId - Run ID
 * @returns Updated run
 */
export const runCancel = async( runId: string ): Promise<RunGetRes> => {
  return await callJson<unknown, RunGetRes>( {
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/runs/${ runId }/cancel`
  } );
};

/**
 * Retry a failed run.
 * @param runId - Run ID
 * @param include_reason - Whether to save metadata for each result
 * @returns New run created from retry
 */
export const runRetry = async( runId: string, include_reason?: boolean ): Promise<RunGetRes> => {
  return await callJson<unknown, RunGetRes>( {
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/runs/${ runId }/retry`,
    params: include_reason !== undefined ? { include_reason } : undefined
  } );
};

/**
 * Stream run progress (SSE endpoint).
 *
 * Uses fetch (via executeFetch) rather than EventSource: the gateway authenticates
 * via the `Authorization: Session <token>` header (translated to `x-user-id`
 * downstream), and the browser EventSource API cannot set custom request headers —
 * it can only send cookies. Returning the response body as a ReadableStream lets the
 * caller parse SSE events while still sending auth headers and supporting cancellation.
 * @param runId - Run ID
 * @param signal - Optional AbortSignal to cancel the stream
 * @returns ReadableStream of the SSE response body
 */
export const runProgressStream = async(
  runId: string,
  signal?: AbortSignal
): Promise<ReadableStream<Uint8Array>> => {
  const response = await executeFetch( {
    route: `/v2/evals/runs/${ runId }/progress`,
    method: CallMethodEnums.GET,
    // Signal SSE intent: the gateway's proxy keys streaming off this header, and
    // it tells intermediary proxies not to buffer the response.
    headers: { Accept: 'text/event-stream' },
    signal,
    isProtected: true
  } );

  if( !response.ok ) throw new Error( `HTTP error! status: ${ response.status }` );
  if( !response.body ) throw new Error( 'No response body' );

  return response.body;
};

/**
 * List run results with pagination.
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated run results
 */
export const runResultsList = async( params?: RunResultsListParams ): Promise<RunResultsListRes> => {
  return await callJson<unknown, RunResultsListRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/v2/evals/runs/results',
    params
  } );
};

/**
 * Get total run result counts with optional grouping.
 * @param params - Query parameters for filtering and grouping
 * @returns Grouped result counts
 */
export const runResultsCountTotal = async(
  params?: RunResultsCountTotalParams
): Promise<RunResultsCountTotalRes> => {
  return await callJson<unknown, RunResultsCountTotalRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/v2/evals/runs/results/count',
    params
  } );
};

/**
 * Compare aggregated run results.
 * @param body - Comparison query parameters
 * @returns Comparison results with buckets
 */
export const runResultsCompare = async(
  body: RunResultsCompareReq
): Promise<RunResultsCompareRes> => {
  return await callJson<RunResultsCompareReq, RunResultsCompareRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/runs/results/compare'
  } );
};

// ============================================================================
// SOURCES
// ============================================================================

/**
 * List/search sources with filtering and pagination.
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns Paginated list of sources
 */
export const sourcesGet = async( params?: SourcesSearchParams ): Promise<SourcesSearchRes> => {
  return await callJson<unknown, SourcesSearchRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/v2/evals/sources',
    params
  } );
};

/**
 * Create a new source.
 * @param args - Source creation request
 * @returns Created source
 */
export const sourceCreate = async( args: SourceCreateReq ): Promise<SourceCreateRes> => {
  return await callJson<SourceCreateReq, SourceCreateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/sources'
  } );
};

/**
 * Get a source by ID and version.
 * @param sourceId - Source ID
 * @param version - Version number or 'latest'
 * @returns Source
 */
export const sourceGet = async( sourceId: string, version: number | 'latest' = 'latest' ): Promise<SourceGetRes> => {
  return await callJson<unknown, SourceGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/sources/${ sourceId }`,
    params: { version }
  } );
};

/**
 * Create a new version of an existing source.
 * @param sourceId - Source ID
 * @param args - New version config
 * @returns New version
 */
export const sourceVersionCreate = async(
  sourceId: string,
  args: SourceVersionCreateReq
): Promise<SourceVersionCreateRes> => {
  return await callJson<SourceVersionCreateReq, SourceVersionCreateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/sources/${ sourceId }/versions`
  } );
};

/**
 * Get preview of source data.
 * @param sourceId - Source ID
 * @param version - Version number or 'latest'
 * @param count - Number of examples to preview
 * @returns Source preview
 */
export const sourcePreviewGet = async(
  sourceId: string,
  version: number | 'latest' = 'latest',
  count?: number
): Promise<SourcePreviewGetRes> => {
  const params: Record<string, string> = {};
  if( count ) params.count = count.toString();

  return await callJson<unknown, SourcePreviewGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/sources/${ sourceId }/versions/${ version }/preview`,
    params
  } );
};

/**
 * Get examples from a source.
 * @param sourceId - Source ID
 * @param version - Version number or 'latest'
 * @param exampleId - Example ID to filter
 * @returns Examples
 */
export const exampleGet = async(
  sourceId: string,
  version: number | 'latest',
  exampleId: string
): Promise<Example[]> => {
  return await callJson<unknown, Example[]>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/sources/${ sourceId }/versions/${ version }/examples`,
    params: {
      example_ids: exampleId
    }
  } );
};

/**
 * Update/deprecate a source version.
 * @param sourceId - Source ID
 * @param version - Version number or 'latest'
 * @param args - Update request
 * @returns Updated source
 */
export const sourceVersionUpdate = async(
  sourceId: string,
  version: number | 'latest',
  args: SourceVersionUpdateReq
): Promise<SourceGetRes> => {
  return await callJson<SourceVersionUpdateReq, SourceGetRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.PATCH,
    route: `/v2/evals/sources/${ sourceId }/versions/${ version }`
  } );
};

/**
 * Validate a source version configuration.
 * @param sourceId - Source ID
 * @param version - Version number or 'latest'
 * @param args - Validation request
 * @returns Validation result
 */
export const sourceVersionValidate = async(
  sourceId: string,
  version: number | 'latest',
  args: SourceVersionValidateReq
): Promise<SourceVersionValidateRes> => {
  return await callJson<SourceVersionValidateReq, SourceVersionValidateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/sources/${ sourceId }/versions/${ version }/validate`
  } );
};

/**
 * Validate a source configuration before creation.
 * @param args - Source validation request
 * @returns Validation result
 */
export const sourceValidate = async( args: SourceCreateReq ): Promise<SourceValidateRes> => {
  return await callJson<SourceCreateReq, SourceValidateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/sources/validate'
  } );
};

/**
 * Get diagnostics for a source version.
 * @param sourceId - Source ID
 * @param version - Version number or 'latest'
 * @returns Diagnostics
 */
export const sourceVersionDiagnosticsGet = async(
  sourceId: string,
  version: number | 'latest'
): Promise<SourceDiagnosticsRes> => {
  return await callJson<unknown, SourceDiagnosticsRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/sources/${ sourceId }/versions/${ version }/diagnostics`
  } );
};

/**
 * Get diagnostics for a source configuration.
 * @param args - Source diagnostics request
 * @returns Diagnostics
 */
export const sourceDiagnostics = async( args: SourceDiagnosticsReq ): Promise<SourceDiagnosticsRes> => {
  return await callJson<SourceDiagnosticsReq, SourceDiagnosticsRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/sources/diagnostics'
  } );
};

// ============================================================================
// SCHEDULES
// ============================================================================

/**
 * Create a new schedule.
 * @param body - Schedule creation request
 * @returns Created schedule
 */
export const scheduleCreate = async( body: ScheduleCreateReq ): Promise<ScheduleResponse> => {
  return await callJson<ScheduleCreateReq, ScheduleResponse>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/schedules'
  } );
};

/**
 * List/search schedules with filtering and pagination.
 * @param params - Query parameters
 * @returns Paginated list of schedules
 */
export const schedulesGet = async( params?: SchedulesGetParams ): Promise<SchedulesGetRes> => {
  return await callJson<unknown, SchedulesGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/v2/evals/schedules',
    params
  } );
};

/**
 * Get a single schedule by ID.
 * @param scheduleId - Schedule ID
 * @returns Schedule with details
 */
export const scheduleGet = async( scheduleId: string ): Promise<ScheduleGetRes> => {
  return await callJson<unknown, ScheduleGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/v2/evals/schedules/${ scheduleId }`,
    params: { include_details: 'true' }
  } );
};

/**
 * Delete a schedule.
 * @param scheduleId - Schedule ID
 * @returns Deletion result
 */
export const scheduleDelete = async( scheduleId: string ): Promise<void> => {
  return await callJson<unknown, void>( {
    isProtected: true,
    method: CallMethodEnums.DELETE,
    route: `/v2/evals/schedules/${ scheduleId }`
  } );
};

/**
 * Pause a schedule.
 * @param scheduleId - Schedule ID
 * @returns Updated schedule
 */
export const schedulePause = async( scheduleId: string ): Promise<ScheduleResponse> => {
  return await callJson<unknown, ScheduleResponse>( {
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/schedules/${ scheduleId }/pause`
  } );
};

/**
 * Resume a schedule.
 * @param scheduleId - Schedule ID
 * @returns Updated schedule
 */
export const scheduleResume = async( scheduleId: string ): Promise<ScheduleResponse> => {
  return await callJson<unknown, ScheduleResponse>( {
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/v2/evals/schedules/${ scheduleId }/resume`
  } );
};

/**
 * Pause multiple schedules.
 * @param body - Bulk pause request
 * @returns Pause results
 */
export const schedulesPause = async( body: SchedulesPauseReq ): Promise<SchedulesPauseRes> => {
  return await callJson<SchedulesPauseReq, SchedulesPauseRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/schedules/pause'
  } );
};

/**
 * Resume multiple schedules.
 * @param body - Bulk resume request
 * @returns Resume results
 */
export const schedulesResume = async( body: SchedulesResumeReq ): Promise<SchedulesResumeRes> => {
  return await callJson<SchedulesResumeReq, SchedulesResumeRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/schedules/resume'
  } );
};

/**
 * Delete multiple schedules.
 * @param body - Bulk delete request
 * @returns Delete results
 */
export const schedulesDelete = async( body: SchedulesDeleteReq ): Promise<SchedulesDeleteRes> => {
  return await callJson<SchedulesDeleteReq, SchedulesDeleteRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/v2/evals/schedules/delete'
  } );
};

